<template>
    <service-card
        ref="serviceCard"
        :service-tips="serviceTips"
        :service="service"
        icon="mdi-car"
        :order="order"
        :variants-available="variantsAvailable"
        @show-info="showInfo"
        @modify-service="$emit('modify-service', $event)"
        @minimized-change="minimizedChange"
    >
        <template v-slot:checkbox>
            <slot name="checkbox" />
        </template>
        <template v-slot:icons>
            <slot name="icons" />
        </template>
        <template v-slot:orderInfo>
            <order-info-base
                :service="service"
                :service-check-in="serviceCheckIn"
                :service-check-out="serviceCheckOut"
                :service-image="mainImage"
                :is-package="isPackage"
                :tariff-name="service.serviceDetails[0].tariffName"
                :service-label="serviceDetails.planName"
                :order="order"
                :order-end-date="orderEndDate"
                @cancel-service="$emit('cancel-service', $event)"
                @modify-service="$emit('modify-service', $event)"
            />
        </template>
        <template v-slot:serviceInfo>
            <service-info-dialog v-model="modal" :service="service" :info="info" :image="mainImage">
                <template v-if="info && (serviceDetails.durationDays || itineraryDays?.length)" v-slot:serviceInfo>
                    <div class="mb-1">
                        {{
                            `${$t('duration')}: ${serviceDetails.durationDays} ${$t(
                                'day',
                                serviceDetails.durationDays
                            )}`
                        }}
                    </div>
                    <template v-if="info?.shortDescription">
                        <div v-html="info.shortDescription" />
                    </template>
                    <template v-if="itineraryDays?.length">
                        <TourItineraryDays :accommodations="accommodations" :itinerary-days="itineraryDays" />
                    </template>
                </template>
            </service-info-dialog>
        </template>
    </service-card>
</template>

<script lang="ts">
    import {Component, Prop} from 'vue-facing-decorator'
    import ServiceInfoDialog from '~/components/account/orderServices/ServiceInfoDialog.vue'
    import ServiceCard from '~src/components/account/orderServices/serviceCard.src'
    import OrderInfoBase from '~src/components/account/orderServices/orderInfoBase.src'
    import {ServiceBase} from '@/components/account/orderServices/ServiceBase.vue'
    import {clone} from '~/utils/helpers'
    import HotelStars from '~/components/hotels/snippets/HotelStars.vue'
    import TourItineraryDays from '~/components/account/orderServices/TourItineraryDays.vue'

    @Component({
        components: {
            TourItineraryDays,
            HotelStars,
            ServiceCard,
            OrderInfoBase,
            ServiceInfoDialog,
        },
    })
    export class TourService extends ServiceBase {
        @Prop({required: true}) service
        @Prop({default: false}) isPackage

        modal = false
        info = {}
        itineraryDays = []
        accommodations = []
        defaultData = []

        async showInfo() {
            if (!this.infoLoadingFinished) {
                this.loadProductInfo()
            }
            if (this.service.productId) {
                await this.loadItineraryDays()
                await this.loadAccommodations()
            }

            this.modal = true
        }

        async loadItineraryDays() {
            try {
                const res = await this.$api.ownTourContractsItineraryDay.get(this.service.productId)
                this.itineraryDays = res.itineraryDays
            } catch (e) {
                this.itineraryDays = []
            }
        }

        async loadAccommodations() {
            try {
                const res = await this.$api.ownTourContractsAccommodation.get(this.service.productId)
                this.defaultData = res.accommodations
                this.accommodations = await this.prepareData()
            } catch (e) {
                this.accommodations = []
            }
        }

        async prepareData() {
            let data = clone(this.defaultData)
            const processedData = await Promise.all(
                data.map(async accommodation => {
                    let cityId = null
                    let categoryToHotel = []
                    if (accommodation?.cityId) {
                        cityId = await this.getLocation(accommodation?.cityId)
                    }
                    if (accommodation.categoryToHotel.length) {
                        categoryToHotel = await Promise.all(
                            accommodation.categoryToHotel.map(async (accommodation, ind) => {
                                let hotelInf = await this.getHotel(accommodation.hotelId)
                                let categoryInf = await this.getCategory(accommodation.categoryId)
                                return {
                                    hotelInf,
                                    categoryInf,
                                }
                            })
                        )
                    }

                    return {
                        ...accommodation,
                        cityId,
                        categoryToHotel,
                    }
                })
            )
            return processedData
        }

        async getLocation(cityId) {
            try {
                const {
                    cities: [city],
                } = await this.$api.locations.get({id: cityId, limit: 1})
                return `${city.name}, ${city.countryName}`
            } catch (e) {
                return null
            }
        }

        async getHotel(hotelId) {
            const {
                hotels: [hotel],
            } = await this.$api.hotels.getFull({
                hotelId,
                contentProviderId: 1,
                limit: 1,
            })
            return hotel
        }

        async getCategory(id) {
            const {
                categories: [category],
            } = await this.$api.ownProductServiceCategories.get({
                id,
            })
            return category
        }

        async loadProductInfo() {
            this.isInfoLoading = true
            const processId = this.service.processId.replace(/^.{6}/, '')

            try {
                this.info = await this.$api.tourInfo.get({processId})
                // eslint-disable-next-line no-empty
            } catch (e) {}

            this.isInfoLoading = false
            this.infoLoadingFinished = true
        }

        get mainImage() {
            if (this.info.images && this.info.images.length) {
                let image = this.info.images.find(el => el.mainImage)
                if (!image) {
                    image = this.info.images[0]
                }
                return image.url
            }
            return null
        }

        get serviceCheckIn() {
            const date = this.$filters.dateFormat(this.service.startDateTime)
            return `<span>${date}</span>`
        }

        get serviceCheckOut() {
            const date = this.$filters.dateFormat(this.service.endDateTime)
            return `<span>${date}</span>`
        }

        get serviceDetails() {
            return this.service.serviceDetails[0]
        }

        get isTO1() {
            return authStore.isTO1
        }
    }

    export default TourService
</script>
